import { TabletButton } from '@campfire/tablet-button';
import { DialogActions, DialogContent, Typography } from '@material-ui/core';
import { CheckCircle, EventNote as EventNoteIcon } from '@material-ui/icons';
import React from 'react';
import { Link } from 'react-router-dom';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

interface EventEnrolmentResultDialogProps {
  open: boolean;
  onClose: () => void;
  activityName: string;
  sessionCount: number;
  dateCount: number;
  hasOpenRoster: boolean;
  isAutoRostered: boolean;
  onSelectSessions?: () => void;
}

export const EventEnrolmentResultDialog = ({
  open,
  onClose,
  activityName,
  sessionCount,
  dateCount,
  hasOpenRoster,
  isAutoRostered,
  onSelectSessions,
}: EventEnrolmentResultDialogProps) => {
  const { isMobile, theme } = useCampfireTheme();
  const isSingleSessionSingleDate = sessionCount <= 1 && dateCount <= 1;

  return (
    <FullscreenDialog open={open} close={onClose} fullScreen={isMobile} title='Event Enrolment' scroll='paper'>
      <DialogContent style={{ minWidth: isMobile ? 290 : 420, paddingTop: 32, textAlign: 'center' }}>
        {isAutoRostered ? (
          <>
            <CheckCircle style={{ fontSize: 56, color: theme.palette.success.main, marginBottom: 16 }} />
            <Typography variant='h6' style={{ fontWeight: 600, marginBottom: 8 }}>
              {"You're rostered!"}
            </Typography>
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
              {"You've been automatically rostered into"} <strong>{activityName}</strong>.
              {" You'll receive a notification if any changes are made to your roster."}
            </Typography>
          </>
        ) : isSingleSessionSingleDate ? (
          <>
            <CheckCircle style={{ fontSize: 56, color: theme.palette.success.main, marginBottom: 16 }} />
            <Typography variant='h6' style={{ fontWeight: 600, marginBottom: 8 }}>
              {"You've joined!"}
            </Typography>
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
              {"You've been marked as available for"} <strong>{activityName}</strong>.
              {" The activity manager will finalise the roster and you'll be notified."}
            </Typography>
          </>
        ) : (
          <>
            <EventNoteIcon style={{ fontSize: 56, color: theme.palette.primary.main, marginBottom: 16 }} />
            <Typography variant='h6' style={{ fontWeight: 600, marginBottom: 8 }}>
              {"You've joined!"} Now pick your dates.
            </Typography>
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
              <strong>{activityName}</strong> has {dateCount} date{dateCount !== 1 ? 's' : ''} with {sessionCount}{' '}
              session{sessionCount !== 1 ? 's' : ''} available.
            </Typography>
            <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
              {hasOpenRoster
                ? 'Select which dates and sessions you want to attend. You will be rostered immediately.'
                : 'Select which dates and sessions you are available for. The manager will finalise the roster.'}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', justifyContent: 'center' }}>
        {isSingleSessionSingleDate || isAutoRostered ? (
          <Link style={{ color: 'inherit', textDecoration: 'none' }} to='my-activities'>
            <TabletButton
              variant='contained'
              color='primary'
              size='medium'
              onClick={onClose}
              style={{ textTransform: 'none' }}
            >
              View My Rosters
            </TabletButton>
          </Link>
        ) : (
          <>
            <TabletButton size='medium' onClick={onClose} style={{ textTransform: 'none', marginRight: 8 }}>
              {"I'll do this later"}
            </TabletButton>
            <TabletButton
              variant='contained'
              color='primary'
              size='medium'
              onClick={() => {
                onClose();
                if (onSelectSessions) onSelectSessions();
              }}
              style={{ textTransform: 'none' }}
            >
              Select Dates
            </TabletButton>
          </>
        )}
      </DialogActions>
    </FullscreenDialog>
  );
};
